import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ISwapResolvedMessage } from '../../components';
import { Routes } from '../../constants/routes';
import { INFT } from '../../types/nft';
import { ISwap } from '../../types/swap';

export enum AppState {
  DEFAULT,
  CREATING_SWAP,
  ANSWER_SWAP,
  SWAP_RESOLVED,
}

export enum CreateSwapStep {
  PICK_NFT,
  CHOSE_RECIPIENT,
}

export enum AnswerSwapStep {
  VIEW_SWAP,
  PICK_NFT,
  SWAP_REVIEW,
  FINAL_SWAP_CONFIRMATION,
}

interface IAppState {
  appState: AppState;
  createSwapStep: CreateSwapStep;
  answerSwapStep: AnswerSwapStep;
  selectedNFTs: {
    NFTs: INFT[];
    eth: {
      isInputOpen: boolean;
      amount?: number;
    };
  };
  recipient?: string;
  swapResolvedData: ISwapResolvedMessage;
  activeSwap?: ISwap;
  ethPrice: string;
  walletDropdownOpen: boolean;
  redirectRouteAfterLoginSuccess: Routes;
}

const initialState: IAppState = {
  appState: AppState.DEFAULT,
  createSwapStep: CreateSwapStep.PICK_NFT,
  answerSwapStep: AnswerSwapStep.VIEW_SWAP,
  selectedNFTs: {
    NFTs: [],
    eth: {
      isInputOpen: false,
    },
  },
  recipient: undefined,
  swapResolvedData: {
    type: 'success',
    message: '',
    annotations: 'toOpenSwaps',
  },
  activeSwap: undefined,
  ethPrice: '',
  walletDropdownOpen: false,
  redirectRouteAfterLoginSuccess: Routes.YOUR_NFTS,
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    storeAppState: (state, action: PayloadAction<AppState>): void => {
      state.appState = action.payload;
    },
    storeCreateSwapStep: (state, action: PayloadAction<CreateSwapStep>): void => {
      state.createSwapStep = action.payload;
    },
    storeAnswerSwapStep: (state, action: PayloadAction<AnswerSwapStep>): void => {
      state.answerSwapStep = action.payload;
    },
    addOrRemoveSelectedNFT: (state, action: PayloadAction<INFT>): void => {
      const indexOf = state.selectedNFTs.NFTs.findIndex((NFT) => NFT.id === action.payload.id);
      if (indexOf !== -1) {
        state.selectedNFTs.NFTs.splice(indexOf, 1);
      } else {
        state.selectedNFTs.NFTs.push(action.payload);
      }
    },
    removeSelectedNFT: (state, action: PayloadAction<INFT>): void => {
      const indexOf = state.selectedNFTs.NFTs.findIndex((NFT) => NFT.id === action.payload.id);
      if (indexOf !== -1) {
        state.selectedNFTs.NFTs.splice(indexOf, 1);
      }
    },
    changeNFTAmount: (state, action: PayloadAction<{ id: string; amount: string }>): void => {
      const indexOf = state.selectedNFTs.NFTs.findIndex((NFT) => NFT.id === action.payload.id);
      state.selectedNFTs.NFTs[indexOf].amount = action.payload.amount;
    },
    setEthInputOpen: (state, action: PayloadAction<boolean>): void => {
      state.selectedNFTs.eth.isInputOpen = action.payload;
    },
    addEth: (state, action: PayloadAction<number>): void => {
      state.selectedNFTs.eth.amount = action.payload;
    },
    clearSelectedNFTs: (state): void => {
      state.selectedNFTs = {
        NFTs: [],
        eth: {
          isInputOpen: false,
        },
      };
    },
    addRecipient: (state, action: PayloadAction<string>): void => {
      state.recipient = action.payload;
    },
    storeSwapResolvedData: (state, action: PayloadAction<ISwapResolvedMessage>): void => {
      state.swapResolvedData = action.payload;
    },
    setActiveSwap: (state, action: PayloadAction<ISwap>): void => {
      state.activeSwap = action.payload;
    },
    resetAppState: (): IAppState => initialState,
    setEthPrice: (state, action: PayloadAction<string>): void => {
      state.ethPrice = action.payload;
    },
    setWalletDropdownOpen: (state, action: PayloadAction<boolean>): void => {
      state.walletDropdownOpen = action.payload;
    },
    setRedirectRouteAfterLoginSuccess: (state, action: PayloadAction<Routes>): void => {
      state.redirectRouteAfterLoginSuccess = action.payload;
    },
  },
});
